import { ListSkeleton } from "./LoadingSkeletons";
import { useActiveOrgId } from "../../state/orgs";
import { useOrganizationPeople } from "../../state/governance";
import { MentionPeopleContext } from "./UserMention";
import { ReviewCommentText } from "./ReviewCommentText";
import { use, useRef, useState, type ReactNode } from "react";
import { InspectorIcon } from "../InspectorIcon";
import { createPortal } from "react-dom";
import { ReviewPromptComposer } from "./ReviewPromptComposer";
import type { useSuggestionComments } from "../../state/suggestionComments";
import { Button } from "../ui/button";
import { ComplianceUnavailable } from "./ComplianceUnavailable";
import {
  commentAuthor,
  readInspectorMention,
  reviewCommentText,
  sortReviewTimeline,
  reviewMessageLimit,
  type ReviewRecipient,
} from "./reviewConversationView";
import { Timeline, TimelineItem } from "../ui/timeline";
import { CommentAvatar } from "./CommentAvatar";
import { cn } from "~/lib/utils";

export interface ReviewTimelineEntry {
  id: string;
  createdAt: string;
  order?: number;
  indicator: ReactNode;
  content: ReactNode;
  /** An event's one-liner (a decision, a plan) rather than a message. */
  compact?: boolean;
}

type Props = {
  formatCommentText?: (text: string) => string;
  commentAnchorPrefix?: string;
  composerHost?: HTMLElement | null;
  allowAttachments?: boolean;
  inspectorStatus?: string | null;
  controlError?: string | null;
  onStop?: (() => void) | undefined;
  stopping?: boolean | undefined;
  reservedCharacters?: number;
  initialInspector?: boolean;
  entityId: string;
  label: string;
  conversation: ReturnType<typeof useSuggestionComments>;
  askAI?: (text: string, files?: readonly File[]) => Promise<string | null>;
  activity?: ReadonlyArray<ReviewTimelineEntry>;
  inspectorRequestIds?: ReadonlyArray<string>;
  /** "tabs": the composer rests as one narrow line until focused (the findings page). */
  composerLayout?: "default" | "tabs";
  /** What Inspector is asked about, for the composer's wording. */
  subject?: "finding" | "suggestion";
};
export function ReviewConversation(props: Props) {
  const org = useActiveOrgId();
  const people = useOrganizationPeople(org);
  return (
    <MentionPeopleContext value={people.snapshot?.people ?? []}>
      <ConversationBody key={`${org}:${props.entityId}`} {...props} />
    </MentionPeopleContext>
  );
}
function ConversationBody({
  label,
  conversation,
  askAI,
  activity = [],
  inspectorRequestIds = [],
  initialInspector = false,
  reservedCharacters = 0,
  composerHost,
  allowAttachments = false,
  inspectorStatus,
  controlError,
  onStop,
  stopping,
  commentAnchorPrefix = "",
  formatCommentText = (text) => text,
  composerLayout = "default",
  subject,
}: Props) {
  const { snapshot, unavailableReason, refresh, postComment, hasMore, loadOlder, isLoadingOlder } =
    conversation;
  const people = use(MentionPeopleContext);
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [recipient, setRecipient] = useState<ReviewRecipient>(
    initialInspector ? "inspector" : "comment",
  );
  const [posting, setPosting] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const inspector = recipient === "inspector";
  const limit = reviewMessageLimit(recipient) - reservedCharacters;
  const canSend =
    Boolean(draft.trim() || (inspector && files.length)) &&
    draft.trim().length <= limit &&
    (inspector || files.length === 0) &&
    (!inspector || (Boolean(askAI) && !inspectorStatus));
  const post = async () => {
    if (pending.current || !canSend) return;
    pending.current = true;
    setPosting(true);
    setError(null);
    try {
      const failure = await (inspector ? askAI!(draft.trim(), files) : postComment(draft.trim()));
      setError(failure);
      if (failure === null) {
        setDraft("");
        setFiles([]);
      }
    } catch {
      setError("Your message could not be sent. Your draft is kept here; try again.");
    } finally {
      pending.current = false;
      setPosting(false);
    }
  };
  const changeDraft = (text: string) => {
    const mention = readInspectorMention(text);
    if (mention.mentioned && askAI) {
      setRecipient("inspector");
      setDraft(mention.text);
    } else setDraft(text);
    setError(null);
  };
  const composer = (
    <ReviewPromptComposer
      draft={draft}
      files={files}
      {...(allowAttachments ? { onFilesChange: setFiles } : {})}
      onChange={changeDraft}
      recipient={recipient}
      onRecipientChange={(value) => {
        setRecipient(value);
        setError(null);
      }}
      canAskAI={Boolean(askAI)}
      {...(subject ? { subject } : {})}
      posting={posting}
      canSend={canSend}
      onSubmit={() => void post()}
      limit={limit}
      error={error ?? (inspector ? (controlError ?? null) : null)}
      inspectorStatus={inspectorStatus ?? null}
      onStop={onStop}
      stopping={stopping}
      layout={composerLayout}
    />
  );
  return (
    <section aria-label={label} className="space-y-5 text-foreground">
      {unavailableReason !== null ? (
        <ComplianceUnavailable reason={unavailableReason} onRetry={refresh} />
      ) : snapshot === null ? (
        <ListSkeleton rows={2} label="Loading conversation" />
      ) : (
        <>
          {hasMore ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={isLoadingOlder}
              onClick={() => void loadOlder()}
            >
              {isLoadingOlder ? "Loading…" : "Load earlier comments"}
            </Button>
          ) : null}
          {snapshot.comments.length || activity.length ? (
            <Timeline aria-label="Conversation timeline">
              {sortReviewTimeline<ReviewTimelineEntry>([
                ...snapshot.comments.map((comment) => {
                  const content = reviewCommentText(
                    comment.text,
                    inspectorRequestIds.includes(comment.id),
                  );
                  return {
                    id: `comment:${comment.id}`,
                    createdAt: comment.createdAt,
                    order: 0,
                    indicator: (
                      <CommentAvatar
                        actorId={comment.actorId}
                        email={comment.actorEmail}
                        src={comment.actorAvatarUrl ?? null}
                      />
                    ),
                    content: (
                      <article
                        id={`review-comment-${commentAnchorPrefix}${comment.id}`}
                        className="min-w-0 flex-1 scroll-mt-6"
                      >
                        <header className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                          <span className="break-all font-medium text-foreground">
                            {commentAuthor(people, comment.actorId, comment.actorEmail)}
                          </span>
                          <time dateTime={comment.createdAt} className="text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </time>
                        </header>
                        <div
                          className={cn(
                            "rounded-xl border p-4",
                            content.inspector
                              ? "border-primary/15 bg-primary/[0.025]"
                              : "border-border/70 bg-card",
                          )}
                        >
                          {content.inspector ? (
                            <span className="mb-2 inline-flex items-center gap-1 rounded-md bg-primary/8 px-1.5 py-0.5 text-xs font-medium text-primary">
                              <InspectorIcon aria-hidden className="size-3" />
                              Inspector<span className="sr-only"> request</span>
                            </span>
                          ) : null}
                          <p className="whitespace-pre-wrap break-words text-sm leading-6">
                            <ReviewCommentText text={formatCommentText(content.text)} />
                          </p>
                        </div>
                      </article>
                    ),
                  };
                }),
                ...activity,
              ]).map((entry) => (
                <TimelineItem key={entry.id} indicator={entry.indicator} compact={entry.compact ?? false}>
                  {entry.content}
                </TimelineItem>
              ))}
            </Timeline>
          ) : (
            <p className="text-sm leading-6 text-muted-foreground">
              Add context for your team
              {askAI
                ? ", or ask Inspector to propose a change."
                : " or leave a question about this review."}
            </p>
          )}
        </>
      )}
      {composerHost ? createPortal(composer, composerHost) : composer}
    </section>
  );
}
